import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';

interface StructuredDataProps {
  type?: 'website' | 'photographer' | 'artwork' | 'blogPost' | 'product';
  data?: any;
}

const StructuredData: React.FC<StructuredDataProps> = ({ type = 'website', data = {} }) => {
  const { lng } = useParams<{ lng: string }>();
  const currentLanguage = lng || 'en';
  
  const baseUrl = 'https://algranconpablo.com';
  
  const getBusinessStructuredData = () => ({
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": `${baseUrl}#business`,
    "name": currentLanguage === 'en' ? "Pablo Ledesma Photography" : "Pablo Ledesma Fotografía",
    "alternateName": "Al Grano con Pablo",
    "url": baseUrl,
    "logo": `${baseUrl}/images/about.jpg`,
    "image": [
      `${baseUrl}/images/about.jpg`,
      `${baseUrl}/images/projects.jpg`,
      `${baseUrl}/images/people.jpg`
    ],
    "description": currentLanguage === 'en' 
      ? "Professional film photographer specializing in fine art prints for hotels and commercial spaces in Costa del Sol"
      : "Fotógrafo profesional especializado en arte de pared para hoteles y espacios comerciales en Costa del Sol",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Marbella",
      "addressRegion": "Andalusia", 
      "addressCountry": "ES"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 36.5101,
      "longitude": -4.8850
    },
    "telephone": "+34679581425",
    "email": "algranoconpablo@gmail.com",
    "priceRange": "€€",
    "serviceArea": [
      {
        "@type": "Place",
        "name": "Costa del Sol"
      },
      {
        "@type": "Place", 
        "name": "Marbella"
      },
      {
        "@type": "Place",
        "name": "Estepona"
      },
      {
        "@type": "Place",
        "name": "Málaga"
      }
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": currentLanguage === 'en' ? "Photography Services" : "Servicios de Fotografía",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": currentLanguage === 'en' ? "Fine Art Photography" : "Fotografía Artística",
            "description": currentLanguage === 'en' 
              ? "Professional film photography for hotels and commercial spaces"
              : "Fotografía analógica profesional para hoteles y espacios comerciales"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Product",
            "name": currentLanguage === 'en' ? "Fine Art Prints" : "Impresiones de Arte",
            "description": currentLanguage === 'en'
              ? "High-quality fine art prints for wall decoration"
              : "Impresiones de arte de alta calidad para decoración de paredes"
          }
        }
      ]
    },
    "sameAs": [
      "https://www.instagram.com/algranoconpablo",
      "https://www.youtube.com/@algranoconPablo"
    ]
  });

  const getWebsiteStructuredData = () => ({
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${baseUrl}#website`,
    "url": baseUrl,
    "name": currentLanguage === 'en' ? "Pablo Ledesma Photography" : "Pablo Ledesma Fotografía",
    "description": currentLanguage === 'en'
      ? "Professional film photographer in Marbella specializing in fine art prints"
      : "Fotógrafo profesional en Marbella especializado en impresiones de arte",
    "publisher": {
      "@id": `${baseUrl}#business`
    },
    "inLanguage": [
      {
        "@type": "Language",
        "name": "English",
        "alternateName": "en"
      },
      {
        "@type": "Language", 
        "name": "Spanish",
        "alternateName": "es"
      }
    ]
  });

  const getPhotographerStructuredData = () => ({
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": `${baseUrl}#photographer`,
    "name": "Pablo Ledesma",
    "jobTitle": currentLanguage === 'en' ? "Professional Photographer" : "Fotógrafo Profesional",
    "description": currentLanguage === 'en'
      ? "Film photographer specializing in fine art photography and commercial imagery"
      : "Fotógrafo analógico especializado en fotografía artística e imágenes comerciales",
    "image": `${baseUrl}/images/about.jpg`,
    "url": baseUrl,
    "sameAs": [
      "https://www.instagram.com/algranoconpablo",
      "https://www.youtube.com/@algranoconPablo"
    ],
    "knowsAbout": [
      "Film Photography",
      "Fine Art Photography", 
      "Commercial Photography",
      "Analog Photography"
    ],
    "workLocation": {
      "@type": "Place",
      "name": "Costa del Sol, Spain"
    }
  });

  const getArtworkStructuredData = (artworkData: any) => ({
    "@context": "https://schema.org",
    "@type": "VisualArtwork",
    "name": artworkData.name,
    "description": artworkData.description,
    "creator": {
      "@id": `${baseUrl}#photographer`
    },
    "image": artworkData.image,
    "artMedium": "Film Photography",
    "artform": "Photography",
    "dateCreated": artworkData.dateCreated,
    "locationCreated": {
      "@type": "Place",
      "name": artworkData.location || "Costa del Sol, Spain"
    }
  });

  const getBlogPostStructuredData = (blogData: any) => ({
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": blogData.title,
    "description": blogData.description,
    "image": blogData.image,
    "author": {
      "@id": `${baseUrl}#photographer`
    },
    "publisher": {
      "@id": `${baseUrl}#business`
    },
    "datePublished": blogData.datePublished,
    "dateModified": blogData.dateModified,
    "mainEntityOfPage": blogData.url,
    "inLanguage": currentLanguage
  });

  const getStructuredData = () => {
    const baseStructuredData = [
      getWebsiteStructuredData(),
      getBusinessStructuredData(),
      getPhotographerStructuredData()
    ];

    switch (type) {
      case 'artwork':
        return [...baseStructuredData, getArtworkStructuredData(data)];
      case 'blogPost':
        return [...baseStructuredData, getBlogPostStructuredData(data)];
      case 'product':
        return [...baseStructuredData, data];
      default:
        return baseStructuredData;
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(getStructuredData())}
      </script>
    </Helmet>
  );
};

export default StructuredData;